import { useCallback, useEffect, useState } from "react";
import { ApolloError, useApolloClient } from "@apollo/client";
import {
  GET_NEWS_PROCESSOR_HEALTH,
  REFRESH_NEWS_PROCESSOR_HEALTH,
} from "../../../graphql/health";

export interface NewsProcessorHealth {
  status: "healthy" | "unhealthy" | "unreachable";
  lastChecked: string;
  responseTime: number;
  details?: Record<string, any>;
  error?: string;
}

interface UseHealthPollerOptions {
  enabled?: boolean;
  pollingInterval?: number;
}

export function useHealthPoller(options: UseHealthPollerOptions = {}) {
  const { enabled = true, pollingInterval: customInterval = 30000 } = options;
  const client = useApolloClient();

  const [health, setHealth] = useState<NewsProcessorHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load persisted polling interval from localStorage
  const [pollingInterval, setPollingInterval] = useState(customInterval);

  useEffect(() => {
    const saved = localStorage.getItem("healthCheckInterval");
    if (saved) {
      const parsed = parseInt(saved, 10);
      // Validate that the interval is within reasonable bounds (5s to 5min)
      if (parsed >= 5000 && parsed <= 300000) {
        setPollingInterval(parsed);
      } else {
        // Reset to default if invalid
        localStorage.removeItem("healthCheckInterval");
      }
    }
  }, []);

  const mapHealth = useCallback(
    (payload: {
      status: "healthy" | "unhealthy" | "unreachable";
      lastChecked: string;
      responseTime: number;
      details?: string | null;
      error?: string | null;
    }): NewsProcessorHealth => {
      let parsedDetails: Record<string, any> | undefined;

      if (payload.details) {
        try {
          parsedDetails = JSON.parse(payload.details);
        } catch {
          parsedDetails = undefined;
        }
      }

      return {
        status: payload.status,
        lastChecked: payload.lastChecked,
        responseTime: payload.responseTime,
        error: payload.error || undefined,
        details: parsedDetails,
      };
    },
    [],
  );

  const fetchHealth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data } = await client.query<{
        newsProcessorHealth?: {
          status: "healthy" | "unhealthy" | "unreachable";
          lastChecked: string;
          responseTime: number;
          details?: string | null;
          error?: string | null;
        };
      }>({
        query: GET_NEWS_PROCESSOR_HEALTH,
        fetchPolicy: "network-only",
      });

      if (!data?.newsProcessorHealth) {
        throw new Error("No health status returned by backend");
      }

      setHealth(mapHealth(data.newsProcessorHealth));
    } catch (err) {
      const message =
        err instanceof ApolloError || err instanceof Error
          ? err.message
          : "Failed to fetch health status";
      setError(message);
      setHealth({
        status: "unreachable",
        lastChecked: new Date().toISOString(),
        responseTime: 0,
        error: message,
      });
    } finally {
      setIsLoading(false);
    }
  }, [client, mapHealth]);

  // Initial fetch
  useEffect(() => {
    if (enabled) {
      fetchHealth();
    }
  }, [enabled, fetchHealth]);

  // Polling
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      fetchHealth();
    }, pollingInterval);

    return () => clearInterval(interval);
  }, [enabled, fetchHealth, pollingInterval]);

  const updatePollingInterval = useCallback((interval: number) => {
    setPollingInterval(interval);
    localStorage.setItem("healthCheckInterval", String(interval));
  }, []);

  const manualRefresh = useCallback(async () => {
    try {
      const { data } = await client.mutate<{
        refreshNewsProcessorHealth?: {
          status: "healthy" | "unhealthy" | "unreachable";
          lastChecked: string;
          responseTime: number;
          details?: string | null;
          error?: string | null;
        };
      }>({
        mutation: REFRESH_NEWS_PROCESSOR_HEALTH,
      });

      if (!data?.refreshNewsProcessorHealth) {
        throw new Error("No refreshed health status returned by backend");
      }

      setHealth(mapHealth(data.refreshNewsProcessorHealth));
      setError(null);
    } catch (err) {
      const message =
        err instanceof ApolloError || err instanceof Error
          ? err.message
          : "Failed to refresh health status";
      setError(message);
    }
  }, [client, mapHealth]);

  return {
    health,
    isLoading,
    error,
    pollingInterval,
    updatePollingInterval,
    manualRefresh,
  };
}
